import { useEffect, useState } from "react";
import { notifyError, notifySuccess } from "src/assets/toastService";
import { getOrderDraft, putLedgerByCustomer, putOrderDraft } from "src/shared/api";
import { useNotificationSocket } from "src/shared/socket/useNotificationSocket";

// ---- List (no id) shape ----
export interface CustomerDetails {
  firstName: string;
  lastName: string;
  middleName: string;
  phones: {
    isPrimary: boolean;
    phone: string;
  }[];
}

export interface DraftOrder {
  _id: string;
  amount: string;
  createdBy: string;
  customerDetails: CustomerDetails;
  customerId: string;
  dateTime: string;
  modifiedBy: string | null;
  modifiedOn: string | null;
  outstanding: number;
  remainingAmount: string;
  approvalStatus: string;
}

export type PendingAmountList = DraftOrder[];

// ---- Single (by id) shape ----
export interface DraftDetailCustomer {
  firstName: string;
  lastName: string;
  middleName: string;
  phoneNumber: string;
}

export interface DraftProduct {
  productName: string;
  arabicName: string;
  itemTotal: number;
  productId: string;
  quantity: number;
  unitPrice: number;
  [key: string]: unknown;
}

export interface DraftPaymentHistory {
  paymentMethod: string;
  receivedAmount: string;
}

export interface DraftPayments {
  creditAdjust: number;
  paymentMethod: string;
  receivedAmount: string;
}

export interface DraftOrderDetail {
  _id: string;
  approvalStatus: string;
  cancelOrderAmount: string;
  createdBy: string;
  createdOn: string;
  customerDetails: DraftDetailCustomer;
  customerId: string;
  deliveryFee: number;
  discount: string;
  finalAmount: string;
  orderAmount: string;
  orderDate: string;
  orderId: string;
  orderStatus: string;
  orderTime: string;
  orgId: string;
  outstandingBalance: number;
  paymentHistory: DraftPaymentHistory[];
  payments: DraftPayments;
  products: DraftProduct[];
  remainingAmount: string;
}

function extractApiError(err: any, fallback: string): string {
  return (
    err?.response?.data?.Message ||
    err?.response?.data?.message ||
    err?.message ||
    fallback
  );
}

export default function useDraft(id?: string, approvalStatus?: string, searchBy?: string) {
  const [draftTable, setDraftTable] = useState<PendingAmountList>([]);
  const [draft, setDraft] = useState<DraftOrderDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [closePopup, setClosePopup] = useState<boolean>(false);
  const now = new Date();
  const { clearDraftOrderUpdates } = useNotificationSocket();
  

  const localDate =
    now.getFullYear() +
    "-" +
    String(now.getMonth() + 1).padStart(2, "0") +
    "-" +
    String(now.getDate()).padStart(2, "0");

  const fetchDraft = async () => {
    try {
      setLoading(true);
      setError(null);
        const response = id
        ? await getOrderDraft(id)
        : await getOrderDraft(undefined, approvalStatus, searchBy);
      clearDraftOrderUpdates();
      if (id) {
        setDraft(response?.result ?? response);
      } else {
        setDraftTable(response?.result ?? response ?? []);
      }
    } catch (err) {
      notifyError(extractApiError(err, "Failed to fetch draft order."));
      // setError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDraft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, approvalStatus, searchBy]);

  const updatesLedgerLimit = (newLimit: number) => {
    if (!draft?.customerId) {
      notifyError("Customer details are not available.");
      return;
    }

    setLoading(true);

    putLedgerByCustomer(draft.customerId, {
      creditLimit: { defaultLimit: newLimit },
      ...(id ? { draftOrderId: id } : {}),
      date: localDate,
      time: now.toTimeString().split(" ")[0],
    })
      .then(() => {
        notifySuccess("Credit limit updated successfully.");
        setClosePopup(true);
      })
      .catch((err) =>
        notifyError(extractApiError(err, "Failed to update credit limit."))
      )
      .finally(() => setLoading(false));
  };

  const draftStatusUpdate = (status: string, denyReason?: string) => {
    if (!id) {
      notifyError("Try again");
      return;
    }

    setLoading(true);

    const sendData = {
      approvalStatus: status,
      ...(denyReason ? { denyReason } : {}),
      date: localDate,
      time: now.toTimeString().split(" ")[0],
    };

    putOrderDraft(id, sendData)
      .then(() => {
        notifySuccess(
          status === "Rejected"
            ? "Order rejected successfully."
            : "Order approved successfully."
        );
        setClosePopup(true);
      })
      .catch((err) =>
        notifyError(extractApiError(err, "Failed to update order status."))
      )
      .finally(() => setLoading(false));
  };

  return {
    draftTable,
    draft,
    loading,
    error,
    updatesLedgerLimit,
    setClosePopup,
    closePopup,
    draftStatusUpdate,
  };
}
